import React, { type MouseEvent } from 'react';

import type { IconsList as Icons } from '@mint-lib/icons/allIcons';
import styled from '@mint-lib/styled';
import ListItemButton, {
  ListItemButtonProps as ListItemButtonPropsM,
} from '@mui/material/ListItemButton';

import Box from '../Box/Box.jsx';
import Icon from '../Icon/Icon.jsx';
import Truncation, { type TruncationProps } from '../Truncation/Truncation.jsx';

export interface SidebarItemProps extends ListItemButtonPropsM {
  /**
   * Text inside menu item
   */
  text?: string;
  /**
   * Icon inside menu item
   */
  icon?: Icons;
  /**
   * Arrow icon inside menu item
   */
  expandable?: boolean;
  /**
   * open state
   */
  open?: boolean;
  /**
   * cb for click on menu item
   */
  onItemClick?: (...args: any) => void;
  /**
   * cb for click on expandable icon
   */
  onChevronClick?: (...args: any) => void;
  /**
   * variant of menu item
   */
  variant?: 'primary' | 'secondary';
  /**
   * add start adornment to the item
   **/
  startAdornment?: React.ReactNode;
  /**
   * add end adornment to the item
   **/
  endAdornment?: React.ReactNode;
  /**
   * add disabled state to the item
   */
  disabled?: boolean;
  /**
   * tooltip placement
   */
  tooltipPlacement?: TruncationProps['tooltipPlacement'];
  /**
   * tooltip visible state
   */
  tooltipOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  text,
  icon,
  expandable = false,
  open,
  tooltipOpen,
  onItemClick,
  onChevronClick,
  variant = 'primary',
  startAdornment,
  endAdornment,
  disabled,
  tooltipPlacement = 'top',
  ...props
}) => {
  const handleChevronClick = (e: MouseEvent) => {
    if (!onChevronClick) {
      return;
    }

    e.stopPropagation();
    onChevronClick(e);
  };
  return (
    <StyledListItemButton
      onClick={(e) => onItemClick && onItemClick(e)}
      $expandable={expandable}
      disabled={disabled}
      {...props}
    >
      {expandable ? (
        <Icon
          component={open ? 'ChevronDown' : 'ChevronRight'}
          sx={{ color: 'text.secondary' }}
          data-testid="chevron-down-icon"
          onClick={handleChevronClick}
        />
      ) : null}
      {icon ? (
        <Icon
          component={icon}
          sx={{
            color: variant === 'primary' ? 'text.primary' : 'text.secondary',
          }}
          data-testid="item-icon"
        />
      ) : null}
      {startAdornment ? startAdornment : null}
      <Truncation
        TooltipProps={{ open: tooltipOpen }}
        tooltipTitle={text}
        tooltipPlacement={tooltipPlacement}
        variant={variant === 'primary' ? 'bodyShort02' : 'bodyShort01'}
        color={(theme) =>
          variant === 'primary'
            ? theme.palette.text.primary
            : theme.palette.text.secondary
        }
      >
        {text}
      </Truncation>
      <Box sx={{ marginLeft: 'auto', display: 'inline-flex' }}>
        {endAdornment}
      </Box>
    </StyledListItemButton>
  );
};

export default SidebarItem;

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'onItemClick' && prop !== '$expandable',
})<{ $expandable?: boolean }>(({ theme, $expandable }) => ({
  color: theme.palette.text.primary,
  width: '11.5rem',
  height: '2rem',
  borderRadius: '4px',
  padding: $expandable ? '0.5rem' : '0.5rem 0.5rem 0.5rem 2rem',

  '&:hover': {
    backgroundColor: theme.palette.ui.hover,
  },
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.ui.selected} !important`,
    '.MuiTypography-root': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  marginTop: '0.5rem',
  gap: '8px',
}));
